import { useMemo, useCallback, useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useInbox } from '@/hooks/use-inbox'
import { useWorkspace } from '@/providers/workspace-provider'
import { useChatSurfaceRegistryOptional } from '@/hooks/use-chat-surface-registry'
import { openInboxThreadInChat } from '@/lib/open-inbox-thread-in-chat'
import { openAgentChatThread } from '@/lib/open-agent-chat-thread'
import { InboxReader } from '@/components/features/inbox/InboxReader'
import { mapInboxItemToThread, mapWorkspaceToInbox, resolveThreadWorkspace } from '@/components/features/inbox/mappers'
import type { Banner, Status } from '@/components/features/inbox/types'
import { testIds } from '@/lib/test-ids'
import { GitConflictActionBar } from '@/components/features/inbox/GitConflictActionBar'
import { getSoulProposalContextDoc } from '@/components/features/inbox/SoulProposalActionBar'

/**
 * Single-thread reader shown in main panel when opened from sidebar inbox (`inbox-thread` tab).
 */
export function InboxThreadMainTab({ threadId }: { threadId: string }) {
  const registry = useChatSurfaceRegistryOptional()
  const { allItems, archive, markUnread, markRead, refetch, hydrateThread } = useInbox()
  const { workspaces: appWorkspaces, activeWorkspaceIds } = useWorkspace()
  const [banner, setBanner] = useState<Banner | null>(null)

  const inboxWorkspaces = useMemo(
    () => appWorkspaces.map((ws) => mapWorkspaceToInbox(ws, activeWorkspaceIds)),
    [appWorkspaces, activeWorkspaceIds],
  )

  const [snoozedIds, setSnoozedIds] = useState<Set<string>>(new Set())
  const [snoozedUntilMap, setSnoozedUntilMap] = useState<Record<string, string>>({})

  const threadsWithSnooze = useMemo(() => {
    return allItems.map(mapInboxItemToThread).map((t) => {
      if (snoozedIds.has(t.id)) {
        return { ...t, status: 'snoozed' as Status, snoozedUntil: snoozedUntilMap[t.id] }
      }
      return t
    })
  }, [allItems, snoozedIds, snoozedUntilMap])

  const activeThread = useMemo(
    () => threadsWithSnooze.find((t) => t.id === threadId) ?? null,
    [threadsWithSnooze, threadId],
  )

  const workspaceAccent = useMemo(() => {
    if (!activeThread) return null
    const w = resolveThreadWorkspace(activeThread, inboxWorkspaces)
    if (!w) return null
    return { label: w.label, color: w.color }
  }, [activeThread, inboxWorkspaces])

  const handleArchive = useCallback(
    (id: string) => {
      archive(id)
      setBanner({ type: 'archive', threadId: id })
    },
    [archive],
  )

  const handleSnooze = useCallback((id: string, _label: string, hint: string) => {
    setSnoozedIds((prev) => new Set([...prev, id]))
    setSnoozedUntilMap((prev) => ({ ...prev, [id]: hint }))
  }, [])

  const handleUnarchive = useCallback(() => {
    void refetch()
  }, [refetch])

  // Lazily create and link ai_conversation when thread is first opened
  useEffect(() => {
    if (!activeThread || activeThread.linkedConversationId) return
    void hydrateThread(threadId)
  }, [threadId, activeThread?.linkedConversationId, activeThread, hydrateThread])

  const handleOpenInChat = useCallback(async (id: string) => {
    const thr = threadsWithSnooze.find((t) => t.id === id)
    const soulCtx = thr ? getSoulProposalContextDoc(thr) : null
    const linked = await openInboxThreadInChat({
      threadId: id,
      title: thr?.title,
      linkedConversationId: thr?.linkedConversationId,
      hydrate: hydrateThread,
      registry,
      forceAgentPanel: true,
      contextDocumentId: soulCtx?.documentId,
      contextDocumentTitle: soulCtx?.title,
    })
    if (!linked) {
      toast.error('Could not open chat for this thread')
    }
  }, [registry, threadsWithSnooze, hydrateThread])

  const handleOpenInNewTab = useCallback((id: string) => {
    const thr = threadsWithSnooze.find((t) => t.id === id)
    const clickEvent = { metaKey: true, ctrlKey: false }
    if (thr?.kind === 'chat') {
      openAgentChatThread(registry, id, { title: thr.title, clickEvent })
      return
    }
    window.dispatchEvent(
      new CustomEvent('open-inbox-thread-main', {
        detail: {
          threadId: id,
          title: thr?.title,
          workspaceId: thr?.workspaceId,
          clickEvent,
        },
      }),
    )
  }, [registry, threadsWithSnooze])

  if (!activeThread) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 p-6 text-center" data-testid={testIds.inbox.threadMainTab}>
        <p className="text-sm text-muted-foreground">
          Thread not found. It may have been archived or removed.
        </p>
        <Button variant="outline" size="sm" type="button" onClick={() => void refetch()}>
          Refresh
        </Button>
      </div>
    )
  }

  return (
    <div
      className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-background"
      data-testid={testIds.inbox.threadMainTab}
    >
      {banner?.type === 'archive' && banner.threadId === threadId ? (
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2 text-xs bg-muted/40 shrink-0">
          <span className="text-muted-foreground">This thread was archived.</span>
          <Button variant="ghost" size="sm" type="button" className="h-7 text-xs" onClick={() => setBanner(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}
      {activeThread.metadata?.kind === 'git_conflict' && !activeThread.metadata?.resolved ? (
        <GitConflictActionBar thread={activeThread} onResolved={() => void refetch()} />
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <InboxReader
          thread={activeThread}
          onDispatchStatusUpdated={() => void refetch()}
          workspaceAccent={workspaceAccent}
          onArchive={handleArchive}
          onSnooze={handleSnooze}
          onUnarchive={handleUnarchive}
          onMarkAsUnread={markUnread}
          onMarkAsRead={markRead}
          onOpenInChat={handleOpenInChat}
          onOpenInNewTab={handleOpenInNewTab}
          onHydrate={(threadId) => hydrateThread(threadId).then((id) => id ?? undefined)}
          chrome="embedded"
        />
      </div>
    </div>
  )
}
